import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, LogOut, Settings, Sparkles, Bookmark, Building, Home } from 'lucide-react';

interface HeaderProps {
  user?: any;
  businessMember?: any;
  onAuthClick: () => void;
  onShowPersonalizedFeed?: () => void;
  onShowBusinessSignup?: () => void;
  onStartReview?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  user,
  businessMember,
  onAuthClick,
  onShowPersonalizedFeed,
  onShowBusinessSignup,
  onStartReview
}) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <header className="top-0 bg-white shadow-sm border-b border-gray-200 h-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Hamburger button - mobile only */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Logo */}
          <div className="flex shrink-0 items-center pr-8">
            <button
              type="button"
              className="flex items-center gap-2 whitespace-nowrap"
              onClick={() => navigate('/')}
              aria-label="Go to Piezit home"
            >
              <img
                src="/icon.svg"
                alt="Piezit Logo"
                className="h-8 w-8"
              />
              <span className="text-xl font-bold text-gray-900">Piezit</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 min-w-0 max-w-lg ml-8 mr-8">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Search businesses..."
              />
            </div>
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            {user && user.interests && user.interests.length > 0 && onShowPersonalizedFeed && (
              <button
                onClick={onShowPersonalizedFeed}
                className="hidden md:flex items-center space-x-2 px-3 py-2 text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors duration-200"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">For You</span>
              </button>
            )}

            {!businessMember && onStartReview && (
              <button
                onClick={user ? (onStartReview) : (onAuthClick)}
                className="hidden md:flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Create Review</span>
              </button>
            )}

            {/* Business Section */}
            {businessMember ? (
              <button
                onClick={() => navigate('/business/dashboard')}
                className="flex items-center space-x-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors duration-200 font-medium"
              >
                <Building className="w-4 h-4" />
                <span className="hidden sm:inline">Business Dashboard</span>
              </button>
            ) : (onShowBusinessSignup && !user) && (
              <button
                onClick={onShowBusinessSignup}
                className="hidden md:flex items-center space-x-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors duration-200 font-medium"
              >
                <Building className="w-4 h-4" />
                <span className="hidden sm:inline">For Businesses</span>
              </button>
            )}

            {user || businessMember ? (

              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user ? (user.firstName?.charAt(0) || user.email?.charAt(0) || 'U') : (businessMember.businessName?.charAt(0) || businessMember.email?.charAt(0) || 'B')}
                    </span>
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">
                      {user ? (
                        user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email
                      ) : businessMember ? (
                        businessMember.businessName ? businessMember.businessName.trim() : businessMember.email
                      ) : null}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user ? (
                        <>
                          {user.verified ? '✓ Verified' : 'Not verified'} • {user.totalReviews} reviews
                        </>
                      ) : <>
                        {businessMember.membershipPlan.name ? businessMember.membershipPlan.name : 'No Plan'} • {businessMember.category}
                      </>}
                    </p>
                  </div>
                </div>

                <div className="relative group hidden md:block">
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                    <Settings className="h-5 w-5" />
                  </button>

                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      {user ? (
                        <button onClick={() => navigate(`/user`)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <User className="w-4 h-4 mr-2" />
                          Profile Settings
                        </button>) : null}
                      <button
                        onClick={() => {
                          localStorage.removeItem('piezit-user');
                          localStorage.removeItem('piezit-bookmarks');
                          localStorage.removeItem('piezit-onboarding-completed');
                          localStorage.removeItem('piezit-organization');
                          localStorage.removeItem('piezit-token');
                          localStorage.removeItem('piezit-org-token');
                          window.location.replace('/');
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>


            ) : (
              <button
                onClick={onAuthClick}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <User className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />

          {/* Sidebar */}
          <div className="fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden">
            <div className="flex flex-col h-full justify-between">
              <div>
                {/* Sidebar header with close button */}
                <div className="flex items-center justify-between p-3 border-b">
                  <button
                    type="button"
                    className="flex items-center gap-2 whitespace-nowrap"
                    onClick={() => { navigate('/'); setSidebarOpen(false); }}
                    aria-label="Go to Piezit home"
                  >
                    <img
                      src="/icon.svg"
                      alt="Piezit Logo"
                      className="h-8 w-8"
                    />
                    <span className="text-xl font-bold text-gray-900">Piezit</span>
                  </button>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Sidebar content - all your menu items */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  <button
                    onClick={() => { navigate('/'); setSidebarOpen(false); }}
                    className="flex w-full items-center space-x-2 px-2 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  >
                    <Home className="w-4 h-4" />
                    <span className="inline">Home</span>
                  </button>

                  {!businessMember && onStartReview && (
                    <button
                      onClick={() => { setSidebarOpen(false); { user ? (onStartReview()) : (onAuthClick()) } }}
                      className="flex w-full items-center space-x-2 px-2 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="inline">Create Review</span>
                    </button>
                  )}


                  {/* Business Section */}
                  {businessMember ? (
                    <button
                      onClick={() => { setSidebarOpen(false); navigate('/business/dashboard'); }}
                      className="flex w-full items-center space-x-2 px-2 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      <Building className="w-4 h-4" />
                      <span className="inline">Business Dashboard</span>
                    </button>
                  ) : (onShowBusinessSignup && !user) && (
                    <button
                      onClick={() => { setSidebarOpen(false); onShowBusinessSignup(); }}
                      className="flex w-full items-center space-x-2 px-2 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      <Building className="w-4 h-4" />
                      <span className="inline">For Businesses</span>
                    </button>
                  )}


                  {user && user.interests && user.interests.length > 0 && onShowPersonalizedFeed && (
                    <button
                      onClick={() => { setSidebarOpen(false); onShowPersonalizedFeed(); }}
                      className="flex w-full items-center space-x-2 px-2 py-2 text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors duration-200"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span className="inline">For You</span>
                    </button>
                  )}
                </div></div>

              <div className="border-t p-4">
                {user || businessMember ? (
                  <div className="flex flex-col gap-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {user ? (user.firstName?.charAt(0) || user.email?.charAt(0) || 'U') : (businessMember.businessName?.charAt(0) || businessMember.email?.charAt(0) || 'B')}
                        </span>
                      </div>
                      <div className="">
                        <p className="text-sm font-medium text-gray-900">
                          {user ? (
                            user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email
                          ) : businessMember ? (
                            businessMember.businessName ? businessMember.businessName.trim() : businessMember.email
                          ) : null}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user ? (
                            <>
                              {user.verified ? '✓ Verified' : 'Not verified'} • {user.totalReviews} reviews
                            </>
                          ) : <>
                            {businessMember.membershipPlan.name ? businessMember.membershipPlan.name : 'No Plan'} • {businessMember.category}
                          </>}
                        </p>
                      </div>
                    </div>

                    <div className="">
                      {/* <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                        <Settings className="h-5 w-5" />
                      </button> */}

                      {/* Dropdown menu */}
                      {/* <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"> */}
                      <div className="py-1">
                        {user && (
                          <button onClick={() => { setSidebarOpen(false); navigate(`/user`); }}
                            className="flex px-2 py-2 w-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                            <User className="w-4 h-4 mr-2" />
                            Profile Settings
                          </button>)}
                        <button
                          onClick={() => {
                            localStorage.removeItem('piezit-user');
                            localStorage.removeItem('piezit-bookmarks');
                            localStorage.removeItem('piezit-onboarding-completed');
                            localStorage.removeItem('piezit-organization');
                            localStorage.removeItem('piezit-token');
                            localStorage.removeItem('piezit-org-token');
                            window.location.reload();
                          }}
                          className="flex px-2 py-2 items-center w-full text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign Out
                        </button>
                      </div>
                      {/* </div> */}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setSidebarOpen(false); onAuthClick(); }}
                    className="flex text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <User className="h-5 w-5 mr-2" />Sign In
                  </button>
                )}
              </div>
              {/* Move all your menu items here */}
              {/* For You, Create Review, Business buttons, User profile, etc. */}

            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
